// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Defensive parsing of the SOS_BUILDER assistant's block replies (PRD §3.5 /
 * implementation plan P3). The assistant is instructed to reply with a
 * SINGLE JSON object per block, but models occasionally wrap the object in
 * markdown code fences or surround it with prose — strip both before
 * parsing rather than failing the whole generate action.
 */

/** Remove a wrapping ``` / ```json markdown fence, when present. */
export function stripCodeFences(text: string): string {
  const trimmed = text.trim();
  const fenced = /^```(?:json)?\s*([\s\S]*?)\s*```$/.exec(trimmed);
  return fenced ? fenced[1].trim() : trimmed;
}

/**
 * Parse the assistant reply into a JSON object. Tries the fence-stripped
 * text first; falls back to the outermost `{...}` span so a reply with
 * leading/trailing prose still parses. Throws when no JSON object can be
 * recovered — callers surface that as a "try again or edit manually" error
 * and leave the advisor's fields untouched.
 */
export function parseAiJsonObject(reply: string): Record<string, unknown> {
  const stripped = stripCodeFences(reply);
  const candidates = [stripped];
  const start = stripped.indexOf('{');
  const end = stripped.lastIndexOf('}');
  if (start !== -1 && end > start) {
    candidates.push(stripped.slice(start, end + 1));
  }
  for (const candidate of candidates) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Try the next candidate.
    }
  }
  throw new Error('AI reply is not a JSON object');
}
